<template>
    <div :class="['z-share', `z-share--theme-${theme}`]">
        <div class="z-share__trigger"
             :class="{'z-share__trigger--opened': isOpened}"
             ref="share"
        >
            <slot name="trigger" />
            <z-button 
                v-if="!$slots.trigger" 
                kind="bordered" 
                size="s" 
                only-icon
                class="z-share__trigger-button"
            >
                <template v-slot:left>
                    <z-icon name="social/share" width="24" height="24" />
                </template>
            </z-button>
            <span v-if="showText" class="u-bold" v-html="text.share"></span>
        </div>
        <div class="z-share__content" ref="content">
            <div class="z-share__title">
                Поделиться ссылкой
            </div>
            <div ref="ya">
            </div>
        </div>
    </div>
</template>

<script setup>
import {computed, onMounted, ref, onUnmounted} from 'vue'
import {useGetter} from '@/composable/useGetter'
import { localize } from '@/utils/i18n'
import tippy from 'tippy.js'

const props = defineProps({
    theme: {
        type: String,
        validator: prop => ['default', 'inline'].includes(prop),
        default: 'default'
    },
    placement: {
        type: String,
        validator: prop => ['top', 'top-start', 'top-end', 'right', 'right-start', 'right-end', 'bottom', 'bottom-start', 'bottom-end', 'left', 'left-start', 'left-end', 'auto', 'auto-start', 'auto-end'].includes(prop),
        default: 'right-start'
    },
    showText: {
        type: Boolean,
        default: false
    },
    services: {
        type: Array,
        required: true
    },
    url: {
        type: String,
        default: ''
    },
    title: {
        type: String,
        default: ''
    },
    description: {
        type: String,
        default: ''
    },
    image: {
        type: String,
        default: undefined
    },
    trigger: {
        type: String,
        validator: prop => ['click', 'mouseenter', 'focus'].includes(prop),
        default: 'click'
    }
})

const { device } = useGetter(['device'])

const urlYaShare = 'https://yastatic.net/share2/share.js'
const text = {
    share: localize({
        ru: 'Поделиться',
        en: 'Share',
        cn: 'Share'
    })
}

const isOpened = ref(false)
const isYaLoaded = ref(false)
const ya = ref(null)
const share = ref(null)
const content = ref(null)

const getEventForDevice = computed(() => {
    if (['mobile', 'v-tablet'].includes(device.value)) {
        return 'click'
    } else {
        return props.trigger
    }
})

const addYaScript = () => {
    const shareScript = document.querySelector('script[src="https://yastatic.net/share2/share.js"]')
    return new Promise((resolve) => {
        if (!shareScript) {
            const script = document.createElement('script')
            script.async = true
            script.src = urlYaShare
            document.head.appendChild(script)
            script.onload = () => {
                isYaLoaded.value = true
                resolve()
            }
        } else {
            window.addEventListener('load', loadedScriptCallback)
            resolve()

        }
    })
}

const loadedScriptCallback = () => {
    isYaLoaded.value = true
    initYaShare()
}

const initYaShare = async () => {
    await addYaScript()
    if (isYaLoaded.value) {
        window.Ya.share2(ya.value, {
            content: {
                url: props.url || window.location.href,
                title: props.title || document.title,
                description: props.description,
                image: props.image
            },
            theme: {
                bare: true, // отключает стили библиотеки
                lang: localize({
                    ru: 'ru',
                    en: 'en',
                    cn: 'en' // YaShare2 не поддерживает китайский язык, поэтому en - https://yandex.ru/dev/share/doc/ru/
                }),
                services: props.services.join(',')
            }
        })
    }
}
const initTippy = () => {
    tippy(share.value, {
        content: content.value,
        placement: props.placement,
        arrow: false,
        trigger: getEventForDevice.value,
        interactive: true,
        appendTo: 'parent',
        theme: `share-${props.theme}`,
        onShow () {
            isOpened.value = true
        },
        onHide () {
            isOpened.value = false
        }
    })
}

onMounted(() => {
    initTippy()
    initYaShare()
})

onUnmounted(() => {
    window.removeEventListener('load', loadedScriptCallback)
})
</script>
<style lang="scss" src="./index.scss"></style>
